import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface LegalDocument {
  id: string;
  type: string;
  version: string;
  content: string;
  published_at: string;
  is_active: boolean;
}

interface PendingDocuments {
  privacyPolicy: LegalDocument | null;
  terms: LegalDocument | null;
}

export const useLegalDocumentsCheck = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocuments>({
    privacyPolicy: null,
    terms: null,
  });
  const [isChecking, setIsChecking] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const checkLegalDocumentsAcceptance = async () => {
      if (authLoading) return;
      
      if (!user) {
        setNeedsAcceptance(false);
        setIsChecking(false);
        return;
      }

      try {
        // Get active legal documents
        const { data: activeDocs, error: docsError } = await supabase
          .from("legal_documents")
          .select("*")
          .eq("is_active", true);

        if (docsError || !activeDocs) {
          setNeedsAcceptance(false);
          setIsChecking(false);
          return;
        }

        const activePrivacyPolicy = activeDocs.find(d => d.type === "privacy_policy") as LegalDocument | undefined;
        const activeTerms = activeDocs.find(d => d.type === "terms") as LegalDocument | undefined;

        // Check user's accepted versions
        const { data: fanProfile, error: profileError } = await supabase
          .from("fan_profiles")
          .select("accepted_privacy_policy_version, accepted_terms_version")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileError) {
          console.error("Error checking fan profile:", profileError);
          setNeedsAcceptance(false);
          setIsChecking(false);
          return;
        }

        const pending: PendingDocuments = {
          privacyPolicy: null,
          terms: null,
        };

        // Check Privacy Policy
        if (activePrivacyPolicy) {
          if (!fanProfile || fanProfile.accepted_privacy_policy_version !== activePrivacyPolicy.version) {
            pending.privacyPolicy = activePrivacyPolicy;
          }
        }

        // Check Terms
        if (activeTerms) {
          if (!fanProfile || fanProfile.accepted_terms_version !== activeTerms.version) {
            pending.terms = activeTerms;
          }
        }

        setPendingDocuments(pending);
        setNeedsAcceptance(!!pending.privacyPolicy || !!pending.terms);
      } catch (err) {
        console.error("Error checking legal documents acceptance:", err);
        setNeedsAcceptance(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkLegalDocumentsAcceptance();
  }, [user, authLoading]);

  const markAsAccepted = () => {
    setNeedsAcceptance(false);
    setPendingDocuments({ privacyPolicy: null, terms: null });
    setIsDismissed(true);
  };

  const dismiss = () => {
    setIsDismissed(true);
  };

  // Show banner only if needs acceptance and not dismissed
  const showBanner = needsAcceptance && !isDismissed && !isChecking;

  return {
    needsAcceptance,
    pendingDocuments,
    isChecking,
    markAsAccepted,
    showBanner,
    dismiss,
  };
};
